import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Card,
  CardContent,
  CardHeader,
  Link,
  Breadcrumbs,
  Alert,
  Button,
  Snackbar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  ContentCopy as ContentCopyIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon,
  Compare as CompareIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Code as CodeIcon,
  Help as HelpIcon,
  Settings as SettingsIcon,
  Psychology as PsychologyIcon,
  Architecture as ArchitectureIcon,
  Update as UpdateIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { vs2015 } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import TableOfContents from '../../components/common/TableOfContents';

// Import new components
import CodeBlock from '../../components/common/CodeBlock';
import { TwoColumnComparison } from '../../components/common/ComparisonBlock';
import ComparisonBlock from '../../components/common/ComparisonBlock';
import EnhancedTable from '../../components/common/EnhancedTable';
import InfoBox from '../../components/common/InfoBox';
import MigrationNotice from '../../components/common/MigrationNotice';
import PrivacyNotice from '../../components/common/PrivacyNotice';
import SectionHeader from '../../components/common/SectionHeader';
import UsageExample from '../../components/common/UsageExample';
  
const AgentToolsDocumentation = () => {
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [showToc, setShowToc] = useState(true);
    const contentRef = useRef(null);
  
    const handleCloseSnackbar = () => {
      setOpenSnackbar(false);
    };
  
    const copyDocumentation = () => {
      if (contentRef.current) {
        const content = contentRef.current.innerText;
        
        if (navigator.clipboard) {
          navigator.clipboard.writeText(content)
            .then(() => {
              setOpenSnackbar(true);
            })
            .catch(err => {
              console.error('Failed to copy: ', err);
            });
        } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = content;
        document.body.appendChild(textArea);
        textArea.select();
        
        try {
          document.execCommand('copy');
          setOpenSnackbar(true);
        } catch (err) {
          console.error('Failed to copy: ', err);
        }
        
        document.body.removeChild(textArea);
      }
    }
  };

  // Updated section in AgentToolsDocumentation.js

const tocSections = [
    { 
      id: 'introduction', 
      title: 'Introduction to Dedicated Tool Agents',
      icon: 'info',
      subsections: [
        { id: 'what-are-dedicated-agents', title: 'What are Dedicated Tool Agents?' },
        { id: 'when-to-use', title: 'When to Use Dedicated Agents' }
      ]
    },
    { 
      id: 'configuring', 
      title: 'Configuring Dedicated Tool Agents',
      icon: 'settings',
      subsections: [
        { id: 'configuration-options', title: 'Configuration Options' },
        { id: 'example-configuration', title: 'Example Configuration' }
      ]
    },
    { 
      id: 'testing', 
      title: 'Testing Dedicated Agents',
      icon: 'science',
      isNew: true
    },
    { 
      id: 'api-reference', 
      title: 'Tool Agent API Reference',
      icon: 'code',
      subsections: [
        { id: 'get-tool-agent', title: 'Get Tool Agent Configuration' },
        { id: 'update-tool-agent', title: 'Update Tool Agent Configuration' },
        { id: 'test-tool-agent', title: 'Test Tool Agent' },
        { id: 'list-models', title: 'List Available Models' },
        { id: 'clear-cache', title: 'Clear Agent Cache' },
        { id: 'common-errors', title: 'Common API Errors' }
      ]
    },
    { 
      id: 'system-prompts', 
      title: 'Writing Effective System Prompts',
      icon: 'psychology',
      subsections: [
        { id: 'system-prompt-guidelines', title: 'System Prompt Guidelines' },
        { id: 'example-system-prompts', title: 'Example System Prompts' }
      ]
    },
    { 
      id: 'best-practices', 
      title: 'Best Practices for Tool Agents',
      icon: 'check_circle',
      subsections: [
        { id: 'when-to-use-best-practices', title: 'When to Use Dedicated Agents' },
        { id: 'model-selection', title: 'Model Selection Guidelines' },
        { id: 'system-prompt-writing', title: 'System Prompt Writing Guidelines' },
        { id: 'performance-considerations', title: 'Performance Considerations' }
      ]
    },
    { 
      id: 'developer-reference', 
      title: 'Developer Reference',
      icon: 'code',
      subsections: [
        { id: 'tool-agent-implementation', title: 'Tool Agent Implementation' },
        { id: 'agent-pool-implementation', title: 'Agent Pool Implementation' },
        { id: 'tool-execution-flow', title: 'Tool Execution Flow' },
        { id: 'database-schema', title: 'Database Schema' }
      ]
    },
    { 
      id: 'migration', 
      title: 'Migration Guide',
      icon: 'update',
      subsections: [
        { id: 'migration-changes', title: 'Key Changes' },
        { id: 'migration-steps', title: 'Migration Steps' },
        { id: 'backwards-compatibility', title: 'Backwards Compatibility' }
      ]
    },
    { 
      id: 'conclusion', 
      title: 'Conclusion',
      icon: 'summarize'
    }
  ];
  
  // In the render section, update the TableOfContents usage:
  {showToc && (
    <TableOfContents 
      sections={tocSections} 
      showProgress={true}
      showReadingTime={true}
      showSearch={true}
      showBookmarks={true}
    />
  )}

  // Render
  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 3 }}>
        <Link component={RouterLink} to="/dashboard" color="inherit">
          Dashboard
        </Link>
        <Link component={RouterLink} to="/tools" color="inherit">
          Tools
        </Link>
        <Typography color="text.primary">Agent Tools Documentation</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Dedicated Tool Agents Documentation
        </Typography>
        <Box>
          <Button 
            variant="outlined" 
            startIcon={showToc ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setShowToc(!showToc)}
            sx={{ mr: 1 }}
          >
            {showToc ? 'Hide' : 'Show'} Table of Contents
          </Button>        
          <Button 
            variant="contained" 
            startIcon={<ContentCopyIcon />} 
            onClick={copyDocumentation}
          >
            Copy Documentation
          </Button>
          <Button
            variant="outlined"
            component={RouterLink}
            to="/tools/documentation"
            sx={{ ml: 1 }}
            >
            Tool Docs
           </Button>
        </Box>
      </Box>
      {showToc && <TableOfContents sections={tocSections} />}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message="Documentation copied to clipboard"
      />

      {/* Documentation content - all wrapped in a div with ref for copying */}
      <div ref={contentRef}>
        {/* Section 1: Introduction to Agent Tools */}
        <Card sx={{ mb: 4 }} id="introduction">
          <CardHeader title="Dedicated Tool Agents" />
          <CardContent>
            <Typography paragraph>
              The tool system supports dedicated AI agents for specific tools, allowing for specialized capabilities and optimized performance.
            </Typography>

            <SectionHeader 
              id="what-are-dedicated-agents" 
              title="What are Dedicated Tool Agents?" 
              level={2}
            />
            
            <Typography paragraph>
              Dedicated tool agents are separate AI instances that can be configured specifically for individual tools. This allows tools to:
            </Typography>
            <Box component="ul" sx={{ pl: 2 }}>
              <li>Use different LLM models than the main agent</li>
              <li>Have specialized system prompts for domain-specific tasks</li>
              <li>Operate with isolated contexts and configurations</li>
              <li>Process requests independently from the main conversation flow</li>
            </Box>

            <InfoBox type="info" title="Note">
              Dedicated agents are cached for 30 minutes to improve performance. If you update a tool's agent configuration, you may need to clear the cache to see the changes take effect.
            </InfoBox>

            <SectionHeader 
              id="when-to-use" 
              title="When to Use Dedicated Agents" 
              level={2}
            />
            
            <Typography paragraph>
              Consider using dedicated agents for tools that:
            </Typography>
            <Box component="ul" sx={{ pl: 2 }}>
              <li>Require specialized knowledge or capabilities</li>
              <li>Need different context windows or model parameters</li>
              <li>Perform complex analysis that benefits from a focused system prompt</li>
              <li>Would benefit from a different model than your main conversation agent</li>
            </Box>

            <TwoColumnComparison
              title="Main Agent vs. Dedicated Tool Agent"
              leftTitle="Main Agent"
              rightTitle="Dedicated Tool Agent"
              leftContent="Handles the overall conversation flow and user interactions across multiple topics and requests."
              rightContent="Optimized for specific tasks with specialized capabilities, focused system prompts, and potentially different models."
              icon={CompareIcon}
            />

            <SectionHeader 
              id="configuring" 
              title="Configuring Dedicated Tool Agents" 
              level={2}
            />
            
            <Typography paragraph>
              When creating or editing a tool, you can configure its dedicated agent with these options:
            </Typography>
            
            <EnhancedTable
              title="Configuration Options"
              headers={["Option", "Description"]}
              rows={[
                ["Requires Dedicated Agent", "Enable this option to give the tool its own agent"],
                ["Dedicated LLM Model", "Select which model the agent should use (e.g., gpt-4, gpt-3.5-turbo)"],
                ["Dedicated System Prompt", "Provide a custom system prompt that specializes the agent for this tool's tasks"],
                ["Agent Configuration", "Additional configuration options like temperature, max_tokens, etc."]
              ]}
            />

            <UsageExample
              title="Tool with Dedicated Agent Example"
              description="Example of a tool that uses a dedicated agent"
              examples={[
                {
                  label: "Python",
                  language: "python",
                  code: `async def analyze_conversation(ctx: RunContext, conversation_text: str) -> Dict[str, Any]:
    """
    Analyze conversation text to extract key information.
    
    This tool uses a dedicated agent with a specialized system prompt
    to perform advanced conversation analysis.
    
    Args:
        ctx: The run context
        conversation_text: The conversation text to analyze
        
    Returns:
        Dictionary containing extracted information and analysis
    """
    try:
        # Your analysis code here
        # The tool will automatically use its dedicated agent if configured
        
        return {
            "status": "success",
            "extracted_topics": ["topic1", "topic2"],
            "sentiment": "positive",
            "key_entities": {"locations": ["New York", "Paris"], "people": ["John", "Mary"]}
        }
    except Exception as e:
        ctx.log(f"Error analyzing conversation: {str(e)}")
        return {
            "status": "error",
            "message": f"Analysis failed: {str(e)}"
        }`
                }
              ]}
            />

            <SectionHeader 
              id="testing" 
              title="Testing Dedicated Agents" 
              level={2}
            />
            
            <Typography paragraph>
              You can test a tool's dedicated agent directly from the admin interface:
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 24, height: 24, mr: 1, fontSize: '0.8rem' }}>1</Avatar>
                <Typography>Navigate to the tool's configuration page</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 24, height: 24, mr: 1, fontSize: '0.8rem' }}>2</Avatar>
                <Typography>Go to the "Dedicated Agent" tab</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 24, height: 24, mr: 1, fontSize: '0.8rem' }}>3</Avatar>
                <Typography>Enter a test message in the "Test Agent" section</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 24, height: 24, mr: 1, fontSize: '0.8rem' }}>4</Avatar>
                <Typography>Click "Test Agent" to see how the dedicated agent responds</Typography>
              </Box>
            </Box>

            <Typography paragraph>
              You can also test dedicated agents programmatically using the API:
            </Typography>
            
            <CodeBlock 
              language="javascript" 
              code={`// Example API call to test a dedicated agent
const response = await fetch('/admin/api/tool-agents/{tool_id}/test', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer your-api-key'
  },
  body: JSON.stringify({
    message: "Your test message for the agent",
    conversation_id: "optional-conversation-id"  // Include to provide conversation history
  })
});

const result = await response.json();
console.log(result);`}
            />
          </CardContent>
        </Card>

        {/* Section 2: Writing Effective System Prompts */}
        <Card sx={{ mb: 4 }} id="system-prompts">
          <CardHeader title="Writing Effective System Prompts" />
          <CardContent>
            <Typography paragraph>
              The system prompt is one of the most important aspects of configuring a dedicated tool agent. It defines the agent's personality, capabilities, and approach to tasks.
            </Typography>

            <SectionHeader 
              id="system-prompt-guidelines" 
              title="System Prompt Guidelines" 
              level={2}
            />
            
            <Box component="ul" sx={{ pl: 2 }}>
              <li>
                <Typography><strong>Be specific about the tool's purpose:</strong> Clearly define what the tool does and what expertise the agent should have.</Typography>
              </li>
              <li>
                <Typography><strong>Define the expected input and output:</strong> Explain what kind of data the agent will receive and what format it should return.</Typography>
              </li>
              <li>
                <Typography><strong>Set the tone and style:</strong> Specify whether the agent should be technical, conversational, analytical, etc.</Typography>
              </li>
              <li>
                <Typography><strong>Include domain knowledge:</strong> Provide relevant domain-specific information that helps the agent perform better.</Typography>
              </li>
              <li>
                <Typography><strong>Define constraints:</strong> Specify any limitations or boundaries the agent should respect.</Typography>
              </li>
            </Box>

            <SectionHeader 
              id="example-system-prompts" 
              title="Example System Prompts" 
              level={2}
            />
            
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">For a Data Analysis Tool</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <CodeBlock 
                  language="markdown" 
                  code={`You are a specialized data analysis assistant. Your purpose is to analyze data provided to you and extract meaningful insights.

When analyzing data:
1. First identify the data structure and key variables
2. Look for patterns, trends, and outliers
3. Provide statistical observations when relevant
4. Summarize your findings in clear, concise language

Always format numerical results with appropriate precision and units. When uncertain about data, acknowledge limitations rather than making unfounded assumptions.

Your responses should be structured, analytical, and focused on the data provided.`}
                />
              </AccordionDetails>
            </Accordion>
            
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">For a Conversation Analysis Tool</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <CodeBlock 
                  language="markdown" 
                  code={`You are a conversation analysis specialist. Your task is to analyze conversations and extract key information including:

- Main topics discussed
- Sentiment and emotional tone
- Questions asked and answers provided
- Action  items or commitments made
- Key entities mentioned (people, places, organizations)
- Potential issues or concerns raised

Present your analysis in a structured format with clear sections. Be objective and focus on the content of the conversation rather than making judgments. If parts of the conversation are unclear, indicate this rather than making assumptions.

Your analysis should be thorough but concise, highlighting the most important elements of the conversation.`}
                />
              </AccordionDetails>
            </Accordion>
        
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">For a Code Generation Tool</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <CodeBlock 
                  language="markdown" 
                  code={`You are a specialized code generation assistant. Your purpose is to generate high-quality, functional code based on requirements provided to you.

When generating code:
1. First understand the requirements thoroughly
2. Choose appropriate language features and patterns
3. Write clean, well-documented code with comments
4. Include error handling where appropriate
5. Explain your implementation choices

Focus on producing code that is:
- Correct and functional
- Readable and maintainable
- Efficient and performant
- Secure and follows best practices

If the requirements are ambiguous, ask clarifying questions or state your assumptions clearly. Always explain any limitations in your implementation.`}
                />
              </AccordionDetails>
            </Accordion>
          </CardContent>
        </Card>

        {/* Section 3: Tool Agent API Reference */}
        <Card sx={{ mb: 4 }} id="api-reference">
          <CardHeader title="Tool Agent API Reference" />
          <CardContent>
            <Typography paragraph>
              The system provides several API endpoints for managing and interacting with dedicated tool agents.
              All endpoints require API key authentication.
            </Typography>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Get Tool Agent Configuration</Typography>
              <Box sx={{ bgcolor: 'success.lighter', color: 'success.darker', px: 1, py: 0.5, borderRadius: 1, display: 'inline-block', mb: 1 }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>GET</Typography>
              </Box>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 2 }}>
                /admin/api/tool-agents/{'{tool_id}'}
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 1 }}>
                Retrieves the current agent configuration for a specific tool.
              </Typography>
              
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Response Example:</Typography>
              <CodeBlock 
                language="json" 
                code={`{
  "status": "success",
  "tool_id": "analyze_conversation",
  "requires_dedicated_agent": true,
  "dedicated_llm_model": "gpt-4",
  "dedicated_system_prompt": "You are a specialized conversation analyzer...",
  "agent_config": {}
}`}
              />
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Update Tool Agent Configuration</Typography>
              <Box sx={{ bgcolor: 'primary.lighter', color: 'primary.darker', px: 1, py: 0.5, borderRadius: 1, display: 'inline-block', mb: 1 }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>PUT</Typography>
              </Box>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 2 }}>
                /admin/api/tool-agents/{'{tool_id}'}
              </Typography>
              
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Request Body:</Typography>
              <CodeBlock 
                language="json" 
                code={`{
  "requires_dedicated_agent": true,
  "dedicated_llm_model": "gpt-4",
  "dedicated_system_prompt": "You are a specialized conversation analyzer...",
  "agent_config": {
    "temperature": 0.2,
    "max_tokens": 1000
  }
}`}
              />
              
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Response Example:</Typography>
              <CodeBlock 
                language="json" 
                code={`{
  "status": "success",
  "message": "Tool agent configuration updated successfully",
  "tool_id": "analyze_conversation"
}`}
              />
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Test Tool Agent</Typography>
              <Box sx={{ bgcolor: 'warning.lighter', color: 'warning.darker', px: 1, py: 0.5, borderRadius: 1, display: 'inline-block', mb: 1 }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>POST</Typography>
              </Box>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 2 }}>
                /admin/api/tool-agents/{'{tool_id}'}/test
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 1 }}>
                Tests a tool's dedicated agent with a sample message.
              </Typography>
              
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Request Body:</Typography>
              <CodeBlock 
                language="json" 
                code={`{
  "message": "Test message for the agent",
  "conversation_id": "optional-conversation-id"
}`}
              />
              
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Response Example:</Typography>
              <CodeBlock 
                language="json" 
                code={`{
  "status": "success",
  "response": {
    "content": "This is the agent's response to your test message",
    "role": "assistant"
  },
  "execution_time": 1.25
}`}
              />
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>List Available Models</Typography>
              <Box sx={{ bgcolor: 'success.lighter', color: 'success.darker', px: 1, py: 0.5, borderRadius: 1, display: 'inline-block', mb: 1 }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>GET</Typography>
              </Box>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 2 }}>
                /admin/api/tool-agents/models/available
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 1 }}>
                Lists available LLM models that can be used for dedicated tool agents.
              </Typography>
              
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Response Example:</Typography>
              <CodeBlock 
                language="json" 
                code={`{
  "status": "success",
  "models": [
    {
      "id": "gpt-4",
      "name": "GPT-4",
      "context_length": 8192
    },
    {
      "id": "gpt-3.5-turbo",
      "name": "GPT-3.5 Turbo",
      "context_length": 4096
    },
    {
      "id": "claude-2",
      "name": "Claude 2",
      "context_length": 100000
    }
  ]
}`}
              />
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Clear Agent Cache</Typography>
              <Box sx={{ bgcolor: 'error.lighter', color: 'error.darker', px: 1, py: 0.5, borderRadius: 1, display: 'inline-block', mb: 1 }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>POST</Typography>
              </Box>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 2 }}>
                /admin/api/tool-agents/cache/clear
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 1 }}>
                Clears the agent cache for all tools or a specific tool.
              </Typography>
              
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Request Body:</Typography>
              <CodeBlock 
                language="json" 
                code={`{
  "tool_id": "optional-tool-id"  // If provided, only clears cache for this tool
}`}
              />
              
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Response Example:</Typography>
              <CodeBlock 
                language="json" 
                code={`{
  "status": "success",
  "message": "Agent cache cleared successfully",
  "cleared_agents": 3
}`}
              />
            </Box>

            <SectionHeader 
              id="common-errors" 
              title="Common API Errors" 
              level={2}
              icon={WarningIcon}
            />
            
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Error: "Invalid model specified"</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography paragraph>
                  This error occurs when the specified model in <code>dedicated_llm_model</code> is not available or accessible.
                </Typography>
                <Typography variant="subtitle2">Solutions:</Typography>
                <Box component="ol" sx={{ pl: 2 }}>
                  <li>
                    <Typography>
                      Check that the model name is correct and available in your subscription by using the 
                      <code>/admin/api/tool-agents/models/available</code> endpoint.
                    </Typography>
                  </li>
                  <li>
                    <Typography>
                      Verify that your API keys for the model provider are correctly configured.
                    </Typography>
                  </li>
                  <li>
                    <Typography>
                      Try using a different model that you know is available.
                    </Typography>
                  </li>
                </Box>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Agent responses are too generic or not specialized enough</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography paragraph>
                  If your dedicated agent isn't providing specialized responses as expected, the system prompt may need improvement.
                </Typography>
                <Typography variant="subtitle2">Solutions:</Typography>
                <Box component="ol" sx={{ pl: 2 }}>
                  <li>
                    <Typography>
                      Make your system prompt more specific about the agent's role and expertise.
                    </Typography>
                  </li>
                  <li>
                    <Typography>
                      Include examples of the type of analysis or responses you expect.
                    </Typography>
                  </li>
                  <li>
                    <Typography>
                      Specify the format you want the responses in.
                    </Typography>
                  </li>
                  <li>
                    <Typography>
                      Lower the temperature setting in <code>agent_config</code> (e.g., to 0.2) for more focused responses.
                    </Typography>
                  </li>
                </Box>
                <Typography variant="subtitle2" sx={{ mt: 2 }}>Example of an improved system prompt:</Typography>
                <CodeBlock 
                  language="text" 
                  code={`You are a specialized financial analysis expert. When analyzing financial data:

1. Always identify key metrics first (revenue, profit, growth rates)
2. Compare current performance to historical trends
3. Highlight any anomalies or concerning patterns
4. Provide specific, actionable recommendations

Format your analysis with clear sections:
- Summary (2-3 sentences)
- Key Metrics Analysis
- Trend Identification
- Risk Assessment
- Recommendations

Be precise with numbers, always specify units, and use financial terminology accurately.`}
                />
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Slow response times from dedicated agents</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography paragraph>
                  If your dedicated agents are responding slowly, there are several potential causes and solutions.
                </Typography>
                <Typography variant="subtitle2">Solutions:</Typography>
                <Box component="ol" sx={{ pl: 2 }}>
                  <li>
                    <Typography>
                      Consider using a faster model (e.g., GPT-3.5-Turbo instead of GPT-4) for less complex tasks.
                    </Typography>
                  </li>
                  <li>
                    <Typography>
                      Reduce the <code>max_tokens</code> parameter if you don't need long responses.
                    </Typography>
                  </li>
                  <li>
                    <Typography>
                      The first call to a dedicated agent will be slower as the agent is initialized. Subsequent calls should be faster.
                    </Typography>
                  </li>
                  <li>
                    <Typography>
                      If you're processing large amounts of text, consider summarizing or chunking it before sending to the agent.
                    </Typography>
                  </li>
                </Box>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Error: "No dedicated agent available for tool"</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography paragraph>
                  This error occurs when you try to use a dedicated agent for a tool that doesn't have one configured.
                </Typography>
                <Typography variant="subtitle2">Solutions:</Typography>
                <Box component="ol" sx={{ pl: 2 }}>
                  <li>
                    <Typography>
                      Check that <code>requires_dedicated_agent</code> is set to <code>true</code> for the tool.
                    </Typography>
                  </li>
                  <li>
                    <Typography>
                      Ensure that <code>dedicated_llm_model</code> is specified.
                    </Typography>
                  </li>
                  <li>
                    <Typography>
                      Verify that the tool configuration was saved successfully.
                    </Typography>
                  </li>
                </Box>
                <Typography variant="subtitle2" sx={{ mt: 2 }}>Example of correct configuration:</Typography>
                <CodeBlock 
                  language="json" 
                  code={`{
  "requires_dedicated_agent": true,
  "dedicated_llm_model": "gpt-3.5-turbo",
  "dedicated_system_prompt": "You are a specialized assistant..."
}`}
                />
              </AccordionDetails>
            </Accordion>
          </CardContent>
        </Card>

        {/* Section 9: Best Practices */}
        <Card sx={{ mb: 4 }} id="best-practices">
          <CardHeader title="Best Practices for Tool Agents" />
          <CardContent>
            <Typography paragraph>
              Follow these best practices to get the most out of dedicated tool agents.
            </Typography>

            <SectionHeader 
              id="when-to-use-best-practices" 
              title="When to Use Dedicated Agents" 
              level={2}
              icon={CheckCircleIcon}
            />
            
            <ComparisonBlock title="Recommended Use Cases">
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <Paper sx={{ p: 2, bgcolor: 'success.lighter', color: 'success.darker' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Good Candidates for Dedicated Agents</Typography>
                  <Box component="ul" sx={{ pl: 2, m: 0 }}>
                    <li>Complex analysis tasks requiring specialized knowledge</li>
                    <li>Tools that benefit from domain-specific system prompts</li>
                    <li>Tasks requiring different models than your main agent</li>
                    <li>Tools that need consistent, structured outputs</li>
                  </Box>
                </Paper>
                <Paper sx={{ p: 2, bgcolor: 'error.lighter', color: 'error.darker' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Not Ideal for Dedicated Agents</Typography>
                  <Box component="ul" sx={{ pl: 2, m: 0 }}>
                    <li>Simple data retrieval or formatting tasks</li>
                    <li>Tools that don't require specialized knowledge</li>
                    <li>High-frequency tools where performance is critical</li>
                    <li>Tools that need the main conversation context</li>
                  </Box>
                </Paper>
              </Box>
            </ComparisonBlock>

            <SectionHeader 
              id="model-selection" 
              title="Model Selection Guidelines" 
              level={2}
              icon={SettingsIcon}
            />
            
            <EnhancedTable
              title="Model Selection Guidelines"
              headers={["Task Type", "Recommended Model", "Considerations"]}
              rows={[
                ["Complex reasoning", "GPT-4", "Higher cost but better reasoning capabilities"],
                ["General analysis", "GPT-3.5-Turbo", "Good balance of performance and cost"],
                ["Code generation", "GPT-4", "Better understanding of code context and requirements"],
                ["Content summarization", "GPT-3.5-Turbo", "Cost-effective for text processing tasks"]
              ]}
            />

            <SectionHeader 
              id="system-prompt-writing" 
              title="System Prompt Writing Guidelines" 
              level={2}
              icon={PsychologyIcon}
            />
            
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CheckCircleIcon sx={{ color: 'success.main', mr: 1 }} />
                <Typography variant="subtitle1">Effective System Prompt Structure</Typography>
              </Box>
              <Box sx={{ pl: 4, mb: 2 }}>
                <Typography paragraph>
                  A well-structured system prompt typically includes:
                </Typography>
                <Box component="ol" sx={{ pl: 2 }}>
                  <li>
                    <Typography><strong>Role definition:</strong> Clearly define the agent's expertise and role</Typography>
                  </li>
                  <li>
                    <Typography><strong>Task guidelines:</strong> Specific instructions for how to approach tasks</Typography>
                  </li>
                  <li>
                    <Typography><strong>Output format:</strong> Expected structure and format of responses</Typography>
                  </li>
                  <li>
                    <Typography><strong>Constraints:</strong> Any limitations or rules the agent should follow</Typography>
                  </li>
                </Box>
              </Box>
            </Box>

            <UsageExample
              title="System Prompt Template"
              description="A template you can adapt for your dedicated agents"
              examples={[
                {
                  label: "Template",
                  language: "markdown",
                  code: `You are a specialized [DOMAIN] expert with deep knowledge of [SPECIFIC AREAS].

Your task is to [MAIN TASK DESCRIPTION]. When performing this task:
1. [STEP 1]
2. [STEP 2]
3. [STEP 3]

Always format your responses as follows:
- [SECTION 1]: [DESCRIPTION]
- [SECTION 2]: [DESCRIPTION]
- [SECTION 3]: [DESCRIPTION]

Important constraints:
- [CONSTRAINT 1]
- [CONSTRAINT 2]

Your tone should be [TONE DESCRIPTION] and your responses should be [LENGTH/DETAIL LEVEL].`
                }
              ]}
            />

            <SectionHeader 
              id="performance-considerations" 
              title="Performance Considerations" 
              level={2}
              icon={ArchitectureIcon}
            />
            
            <Box sx={{ mb: 3 }}>
              <Typography paragraph>
                Keep these performance considerations in mind when working with dedicated agents:
              </Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                <li>
                  <Typography>
                    <strong>Agent Caching:</strong> Agents are cached for 30 minutes by default. Clear the cache using the API endpoint when testing configuration changes.
                  </Typography>
                </li>
                <li>
                  <Typography>
                    <strong>Initialization Time:</strong> The first call to a tool with a dedicated agent may take longer as the agent is initialized.
                  </Typography>
                </li>
                <li>
                  <Typography>
                    <strong>Resource Usage:</strong> Each dedicated agent consumes additional memory. Be mindful of the number of dedicated agents in your system.
                  </Typography>
                </li>
                <li>
                  <Typography>
                    <strong>Cost Management:</strong> Monitor usage of dedicated agents, especially those using more expensive models.
                  </Typography>
                </li>
              </Box>
            </Box>

            <InfoBox type="warning" title="Performance Warning">
              Having too many dedicated agents can impact system performance and increase costs. 
              Only use dedicated agents when there's a clear benefit to having specialized capabilities for a tool.
            </InfoBox>
          </CardContent>
        </Card>

        {/* Section 10: Developer Reference */}
        <Card sx={{ mb: 4 }} id="developer-reference">
          <CardHeader 
            title="Developer Reference" 
            action={
              <Tooltip title="Advanced implementation details for developers">
                <IconButton>
                  <CodeIcon />
                </IconButton>
              </Tooltip>
            }
          />
          <CardContent>
            <Typography paragraph>
              This section provides technical details about the implementation of tool agents for developers who need to work with the system at a lower level.
            </Typography>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Tool Agent Implementation</Typography>
              <Typography paragraph>
                The tool agent system is implemented with these key components:
              </Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                <li>
                  <Typography>
                    <strong>agent_pool.py:</strong> Manages the lifecycle of dedicated agents, including creation, caching, and execution.
                  </Typography>
                </li>
                <li>
                  <Typography>
                    <strong>tool_agents.py:</strong> Provides API endpoints for managing tool agent configurations.
                  </Typography>
                </li>
                <li>
                  <Typography>
                    <strong>tool_registry.py:</strong> Stores tool configurations, including agent settings.
                  </Typography>
                </li>
                <li>
                  <Typography>
                    <strong>tool_execution.py:</strong> Handles the execution flow for tools, determining whether to use a dedicated agent.
                  </Typography>
                </li>
              </Box>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Agent Pool Implementation</Typography>
              <Typography paragraph>
                The agent pool manages dedicated agents with these key functions:
              </Typography>

              <CodeBlock 
                language="python" 
                code={`# Key functions in the agent pool implementation

async def get_tool_agent(tool_id: str) -> Tuple[Optional[Agent], Optional[Dict[str, Any]]]:
    """
    Get or create an agent for a specific tool.
    
    Args:
        tool_id: ID of the tool
        
    Returns:
        Tuple of (Agent instance, Dependencies) for the tool, or (None, None) 
        if tool doesn't need a dedicated agent
    """
    # Implementation details...

async def execute_with_dedicated_agent(
    tool_id: str, 
    user_input: str,
    conversation_id: Optional[str] = None
) -> Dict[str, Any]:
    """
    Execute a user query with a dedicated agent for a specific tool.
    
    Args:
        tool_id: ID of the tool
        user_input: User message to process
        conversation_id: Optional conversation ID for tracking
        
    Returns:
        Agent execution result
    """
    # Implementation details...

async def clear_agent_cache(tool_id: Optional[str] = None) -> Dict[str, Any]:
    """
    Clear the agent cache for a specific tool or all tools.
    
    Args:
        tool_id: Optional ID of the tool to clear cache for
        
    Returns:
        Status of the operation
    """
    # Implementation details...`}
              />

              <InfoBox type="warning" title="Implementation Note">
                The actual implementation in <code>agent_pool.py</code> uses a caching mechanism with a lock for thread safety. 
                The cache stores agents with their creation timestamp and dependencies.
              </InfoBox>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Tool Execution Flow</Typography>
              <Typography paragraph>
                When a tool with a dedicated agent is executed, the system follows this flow:
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Box sx={{ maxWidth: '100%', overflow: 'auto' }}>
                  <svg width="600" height="300" viewBox="0 0 600 300">
                    {/* Flow chart visualization would go here */}
                    <rect x="50" y="20" width="120" height="60" rx="10" fill="#bbdefb" stroke="#1976d2" strokeWidth="2" />
                    <text x="110" y="55" textAnchor="middle" fill="#1976d2" fontWeight="bold">Tool Invocation</text>
                    
                    <path d="M110,80 L110,120" stroke="#1976d2" strokeWidth="2" markerEnd="url(#arrowhead)" />
                    
                    <rect x="50" y="120" width="120" height="60" rx="10" fill="#c8e6c9" stroke="#388e3c" strokeWidth="2" />
                    <text x="110" y="155" textAnchor="middle" fill="#388e3c" fontWeight="bold">Check Agent Config</text>
                    
                    <path d="M170,150 L240,150" stroke="#1976d2" strokeWidth="2" markerEnd="url(#arrowhead)" />
                    <path d="M110,180 L110,220" stroke="#1976d2" strokeWidth="2" markerEnd="url(#arrowhead)" />
                    
                    <rect x="240" y="120" width="120" height="60" rx="10" fill="#ffecb3" stroke="#ffa000" strokeWidth="2" />
                    <text x="300" y="155" textAnchor="middle" fill="#ffa000" fontWeight="bold">Use Global Context</text>
                    
                    <rect x="50" y="220" width="120" height="60" rx="10" fill="#ffecb3" stroke="#ffa000" strokeWidth="2" />
                    <text x="110" y="245" textAnchor="middle" fill="#ffa000" fontWeight="bold">Get Dedicated</text>
                    <text x="110" y="265" textAnchor="middle" fill="#ffa000" fontWeight="bold">Agent</text>
                    
                    <path d="M170,250 L240,250" stroke="#1976d2" strokeWidth="2" markerEnd="url(#arrowhead)" />
                    
                    <rect x="240" y="220" width="120" height="60" rx="10" fill="#e1bee7" stroke="#7b1fa2" strokeWidth="2" />
                    <text x="300" y="245" textAnchor="middle" fill="#7b1fa2" fontWeight="bold">Execute with</text>
                    <text x="300" y="265" textAnchor="middle" fill="#7b1fa2" fontWeight="bold">Dedicated Agent</text>
                    
                    <path d="M300,180 L300,220" stroke="#1976d2" strokeWidth="2" markerEnd="url(#arrowhead)" />
                    <path d="M300,280 L300,320" stroke="#1976d2" strokeWidth="2" markerEnd="url(#arrowhead)" />
                    
                    <rect x="240" y="320" width="120" height="60" rx="10" fill="#ffcdd2" stroke="#d32f2f" strokeWidth="2" />
                    <text x="300" y="355" textAnchor="middle" fill="#d32f2f" fontWeight="bold">Return Result</text>
                    
                    <text x="200" y="140" textAnchor="middle" fill="#666">No</text>
                    <text x="110" y="200" textAnchor="middle" fill="#666">Yes</text>
                    
                    <defs>
                      <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                        <polygon points="0 0, 10 3.5, 0 7" fill="#1976d2" />
                      </marker>
                    </defs>
                  </svg>
                </Box>
              </Box>

              <Typography paragraph>
                The key steps in this flow are:
              </Typography>
              <Box component="ol" sx={{ pl: 2 }}>
                <li>
                  <Typography>
                    Tool is invoked with parameters
                  </Typography>
                </li>
                <li>
                  <Typography>
                    System checks if the tool requires a dedicated agent
                  </Typography>
                </li>
                <li>
                  <Typography>
                    If no dedicated agent is needed, the tool executes with the global context
                  </Typography>
                </li>
                <li>
                  <Typography>
                    If a dedicated agent is needed, the system retrieves or creates the agent
                  </Typography>
                </li>
                <li>
                  <Typography>
                    The tool executes with the dedicated agent's context
                  </Typography>
                </li>
                <li>
                  <Typography>
                    The result is returned to the caller
                  </Typography>
                </li>
              </Box>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Database Schema</Typography>
              <Typography paragraph>
                Tool agent configurations are stored in the database with these fields:
              </Typography>

              <CodeBlock 
                language="sql" 
                code={`-- Tool configuration table including agent settings
CREATE TABLE tools (
    id TEXT PRIMARY KEY,
    name TEXT NOT NULL,
    description TEXT,
    tool_type TEXT NOT NULL,
    enabled BOOLEAN DEFAULT TRUE,
    default_params JSONB DEFAULT '{}',
    requires_dedicated_agent BOOLEAN DEFAULT FALSE,
    dedicated_llm_model TEXT,
    dedicated_system_prompt TEXT,
    agent_config JSONB DEFAULT '{}',
    created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
    updated_at TIMESTAMP WITH TIME ZONE DEFAULT NOW()
);

-- Tool execution logs
CREATE TABLE tool_executions (
    id SERIAL PRIMARY KEY,
    tool_id TEXT REFERENCES tools(id),
    conversation_id TEXT,
    input_params JSONB,
    result JSONB,
    execution_time FLOAT,
    success BOOLEAN,
    error_message TEXT,
    created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW()
);`}
              />
            </Box>
          </CardContent>
        </Card>

        {/* Section 11: Migration Guide */}
        <Card sx={{ mb: 4 }} id="migration">
          <CardHeader title="Migration Guide" />
          <CardContent>
            <MigrationNotice title="Upgrading from v1.x to v2.x">
              If you're upgrading from v1.x to v2.x, there are several changes to the tool agent system that you need to be aware of.
              This guide will help you migrate your existing tools to use the new dedicated agent capabilities.
            </MigrationNotice>

            <SectionHeader 
              id="migration-changes" 
              title="Key Changes" 
              level={2}
              icon={UpdateIcon}
            />
            
            <EnhancedTable
              headers={["Feature", "v1.x", "v2.x"]}
              rows={[
                ["Agent Configuration", "Global settings only", "Per-tool dedicated agents"],
                ["Model Selection", "Single model for all tools", "Different models per tool"],
                ["System Prompts", "Generic prompts only", "Specialized prompts per tool"],
                ["Agent Caching", "No caching", "30-minute agent caching"],
                ["API Endpoints", "Limited tool management", "Comprehensive agent management"]
              ]}
            />

            <SectionHeader 
              id="migration-steps" 
              title="Migration Steps" 
              level={2}
            />
            
            <Box component="ol" sx={{ pl: 2 }}>
              <li>
                <Typography paragraph>
                  <strong>Update database schema:</strong> Run the provided migration script to add the new fields to your tools table.
                </Typography>
                <CodeBlock 
                  language="sql" 
                  code={`ALTER TABLE tools 
ADD COLUMN requires_dedicated_agent BOOLEAN DEFAULT FALSE,
ADD COLUMN dedicated_llm_model TEXT,
ADD COLUMN dedicated_system_prompt TEXT,
ADD COLUMN agent_config JSONB DEFAULT '{}'`}
                />
              </li>
              <li>
                <Typography paragraph>
                  <strong>Update tool definitions:</strong> Review your existing tools and identify which ones would benefit from dedicated agents.
                </Typography>
              </li>
              <li>
                <Typography paragraph>
                  <strong>Configure dedicated agents:</strong> For each tool that needs a dedicated agent, update its configuration with the new fields.
                </Typography>
                <CodeBlock 
                  language="python" 
                  code={`# Example of updating a tool configuration
await db.execute(
    """
    UPDATE tools
    SET requires_dedicated_agent = $1,
        dedicated_llm_model = $2,
        dedicated_system_prompt = $3,
        agent_config = $4
    WHERE id = $5
    """,
    True,  # requires_dedicated_agent
    "gpt-4",  # dedicated_llm_model
    "You are a specialized data analysis assistant...",  # dedicated_system_prompt
    {"temperature": 0.2, "max_tokens": 1000},  # agent_config
    "analyze_data"  # tool_id
)`}
                />
              </li>
              <li>
                <Typography paragraph>
                  <strong>Update API clients:</strong> If you have custom clients that interact with the tool API, update them to use the new endpoints.
                </Typography>
              </li>
              <li>
                <Typography paragraph>
                  <strong>Test your tools:</strong> Use the testing endpoints to verify that your tools work correctly with dedicated agents.
                </Typography>
              </li>
            </Box>

            <PrivacyNotice title="Data Privacy Considerations">
              The new dedicated agent system may change how conversation data is processed. 
              By default, dedicated agents don't have access to the full conversation history unless explicitly provided.
              Review your data handling practices to ensure compliance with your privacy policies.
            </PrivacyNotice>

            <SectionHeader 
              id="backwards-compatibility" 
              title="Backwards Compatibility" 
              level={2}
              icon={SecurityIcon}
            />
            
            <Typography paragraph>
              The v2.x system maintains backwards compatibility with v1.x tools:
            </Typography>
            <Box component="ul" sx={{ pl: 2 }}>
              <li>
                <Typography>
                  Tools without <code>requires_dedicated_agent</code> set will continue to use the global agent.
                </Typography>
              </li>
              <li>
                <Typography>
                  Existing tool execution flows remain unchanged for tools not using dedicated agents.
                </Typography>
              </li>
              <li>
                <Typography>
                  The tool registry API endpoints from v1.x continue to work, with additional endpoints for dedicated agent management.
                </Typography>
              </li>
            </Box>

            <InfoBox type="info" title="Gradual Migration">
              You don't need to migrate all tools at once. You can gradually update tools to use dedicated agents
              as needed, starting with the ones that would benefit most from specialized capabilities.
            </InfoBox>
          </CardContent>
        </Card>

        {/* Section 12: Conclusion */}
        <Card sx={{ mb: 4 }} id="conclusion">
          <CardHeader title="Conclusion" />
          <CardContent>
            <Typography paragraph>
              Dedicated tool agents provide a powerful way to extend your system with specialized AI capabilities. 
              By configuring tools with their own dedicated agents, you can create a more flexible and powerful system 
              that can handle a wide range of tasks with optimal performance.
            </Typography>

            <Typography paragraph>
              Remember these key points when working with dedicated tool agents:
            </Typography>
            <Box component="ul" sx={{ pl: 2 }}>
              <li>
                <Typography>
                  Use dedicated agents for specialized tasks that benefit from custom system prompts or different models
                </Typography>
              </li>
              <li>
                <Typography>
                  Configure your agents with appropriate models and system prompts for their specific tasks
                </Typography>
              </li>
              <li>
                <Typography>
                  Clear the agent cache when testing configuration changes
                </Typography>
              </li>
              <li>
                <Typography>
                  Monitor performance and costs, especially when using more powerful models
                </Typography>
              </li>
            </Box>

            <Box sx={{ mt: 3, p: 2, bgcolor: 'primary.lighter', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                <HelpIcon sx={{ mr: 1, fontSize: '1rem', color: 'primary.main' }} /> Need Help?
              </Typography>
              <Typography variant="body2">
                For additional help or to report issues, please contact the system administrator or refer to the API documentation.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </div>
    </Box>
  );
};

export default AgentToolsDocumentation;